import { useCallback, useEffect, useState } from 'react';

/**
 * How a route drawn as a line is shown over its photo. A route asks a different
 * question from a boulder — not "which holds count" but "where does it go, and
 * how much of the wall around it should be pushed back" — so it has its own
 * cycle rather than borrowing the holds one:
 * - 'focusAndLine': the line, with the wall greyed out beyond its corridor
 * - 'focus': the corridor's colour alone, so the real holds can be read unmarked
 * - 'line': the line over the whole photo, colour everywhere
 * - 'none': the bare photo
 */
export type RouteLineDisplayMode = 'focusAndLine' | 'focus' | 'line' | 'none';

// Cycle order used by the on-image toggle button.
export const ROUTE_LINE_DISPLAY_MODES: RouteLineDisplayMode[] = ['focusAndLine', 'focus', 'line', 'none'];
export const DEFAULT_ROUTE_LINE_DISPLAY_MODE: RouteLineDisplayMode = 'focusAndLine';

const ROUTE_LINE_MODE_STORAGE_KEY = 'climbProfile.routeLineDisplayMode';

/**
 * What a mode actually draws.
 *
 * `hasCorridor` says whether this line has a band of wall to push back at all.
 * A crag's does not — outdoors there are no holds to tell apart, so the line on
 * the photograph *is* the topo — and without it the two focus modes have
 * nothing to show. They would leave the picture bare, and the toggle would look
 * broken. So a corridor-less line always draws its line, and 'focus' becomes
 * the one reading it can honestly offer: the photo with the route on it.
 */
export function routeLineDisplay(
  mode: RouteLineDisplayMode,
  hasCorridor = true
): { line: boolean; focus: boolean } {
  if (!hasCorridor) {
    return { line: mode !== 'none', focus: false };
  }

  return {
    line: mode === 'focusAndLine' || mode === 'line',
    focus: mode === 'focusAndLine' || mode === 'focus',
  };
}

/** Read the persisted mode (shared as the default across all routes). */
export function readRouteLineDisplayMode(): RouteLineDisplayMode {
  try {
    const stored = localStorage.getItem(ROUTE_LINE_MODE_STORAGE_KEY);
    if (stored && (ROUTE_LINE_DISPLAY_MODES as string[]).includes(stored)) {
      return stored as RouteLineDisplayMode;
    }
  } catch {
    /* ignore unavailable localStorage */
  }
  return DEFAULT_ROUTE_LINE_DISPLAY_MODE;
}

/** Persist the mode so it becomes the default for every other route. */
export function writeRouteLineDisplayMode(mode: RouteLineDisplayMode): void {
  try {
    localStorage.setItem(ROUTE_LINE_MODE_STORAGE_KEY, mode);
  } catch {
    /* ignore unavailable localStorage */
  }
}

// Broadcast so every overlay on the page (e.g. the cover and the enlarged
// modal) stays in sync live when the mode is cycled.
const ROUTE_LINE_MODE_EVENT = 'route-line-display-mode-change';

/**
 * Persisted, page-wide route line display mode. Mirrors useHoldsDisplayMode:
 * the mode is stored in localStorage so it is the default for every other
 * route, and all hook instances update together.
 */
export function useRouteLineDisplayMode(): { mode: RouteLineDisplayMode; cycle: () => void } {
  const [mode, setMode] = useState<RouteLineDisplayMode>(readRouteLineDisplayMode);

  useEffect(() => {
    const onChange = (e: Event) => setMode((e as CustomEvent<RouteLineDisplayMode>).detail);
    window.addEventListener(ROUTE_LINE_MODE_EVENT, onChange);
    return () => window.removeEventListener(ROUTE_LINE_MODE_EVENT, onChange);
  }, []);

  const cycle = useCallback(() => {
    setMode((prev) => {
      const next = ROUTE_LINE_DISPLAY_MODES[(ROUTE_LINE_DISPLAY_MODES.indexOf(prev) + 1) % ROUTE_LINE_DISPLAY_MODES.length];
      writeRouteLineDisplayMode(next);
      window.dispatchEvent(new CustomEvent(ROUTE_LINE_MODE_EVENT, { detail: next }));
      return next;
    });
  }, []);

  return { mode, cycle };
}
